/**
 * Browser / PWA notification system.
 * Level 1: Shows native notifications when app is open.
 * Level 2: Web Push via service worker — works even when app is closed.
 */

import { supabase } from "../lib/supabaseClient";

const PERMISSION_KEY = "ametal_notif_permission_asked";
const SUBSCRIPTION_KEY = "ametal_push_subscribed";

// VAPID public key — must match the private key in Supabase Edge Function
const VAPID_PUBLIC_KEY = "BDX3v1P-0ND2x105LDVxGDO3MKUk1OjqYFSzPwaCF7KOVHeNzQoN8i0Ay21ceeC9vvWnvC7VFeAnw08XtafBrvE";

/** Check if browser notifications are supported */
export function isNotificationSupported() {
  return "Notification" in window && "serviceWorker" in navigator;
}

/** Request notification permission (call once, e.g. after first login) */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";

  const result = await Notification.requestPermission();
  localStorage.setItem(PERMISSION_KEY, "true");
  return result;
}

/** Check if we already have permission */
export function hasNotificationPermission() {
  if (!isNotificationSupported()) return false;
  return Notification.permission === "granted";
}

/** Check if we should ask for permission (haven't asked yet) */
export function shouldAskPermission() {
  if (!isNotificationSupported()) return false;
  if (Notification.permission !== "default") return false;
  return !localStorage.getItem(PERMISSION_KEY);
}

/**
 * Show a native notification via the service worker.
 * Falls back to Notification API if SW is not available.
 */
export async function showNativeNotification(title, body, options = {}) {
  if (!hasNotificationPermission()) return;

  try {
    const reg = await navigator.serviceWorker?.ready;
    if (reg) {
      reg.active.postMessage({
        type: "SHOW_NOTIFICATION",
        title,
        body,
        tag: options.tag || `ametal_${Date.now()}`,
        data: {
          url: window.location.origin,
          page: options.page || null,
        },
      });
    } else {
      // Fallback: direct Notification API (doesn't work on all mobile browsers)
      new Notification(title, {
        body,
        icon: "/logo192.png",
        tag: options.tag,
      });
    }
  } catch (err) {
    console.warn("Native notification failed:", err);
  }
}

/**
 * Compare old and new notification arrays, show native alerts for new ones.
 * Call this from a useEffect watching data.notifications.
 */
export function showNewNotifications(prevNotifs, currentNotifs, currentUserId, isExecRole) {
  if (!hasNotificationPermission()) return;
  if (!prevNotifs || !currentNotifs) return;

  const prevIds = new Set(prevNotifs.map(n => n.id));
  const newOnes = currentNotifs.filter(n => {
    if (prevIds.has(n.id)) return false;
    // Only show notifications relevant to this user
    if (!isExecRole && n.to_user_id !== currentUserId) return false;
    return !n.is_read;
  });

  newOnes.forEach(n => {
    showNativeNotification(n.title, n.message || "", {
      tag: `ametal_notif_${n.id}`,
      page: n.page_link || null,
    });
  });
}

// ─── Level 2: Web Push Subscription ───

/** Convert VAPID key from base64url to Uint8Array */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

/**
 * Subscribe this device to Web Push and store in Supabase.
 * Call after login with notification permission granted.
 * @param {string} userId - current user ID
 * @param {string} companyId - current company ID
 */
export async function subscribeToPush(userId, companyId) {
  if (!isNotificationSupported() || !hasNotificationPermission()) return null;

  try {
    const reg = await navigator.serviceWorker.ready;

    // Check for existing subscription
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const subJson = sub.toJSON();
    const endpoint = subJson.endpoint;
    const p256dh = subJson.keys?.p256dh || "";
    const auth = subJson.keys?.auth || "";

    if (!p256dh || !auth) {
      console.warn("Push subscription missing keys");
      return null;
    }

    // Upsert to Supabase
    const { error } = await supabase.from("push_subscriptions").upsert({
      user_id: userId,
      company_id: companyId,
      endpoint,
      p256dh,
      auth,
      user_agent: navigator.userAgent,
      updated_at: new Date().toISOString(),
    }, { onConflict: "endpoint" });

    if (error) {
      console.warn("Failed to save push subscription:", error);
      return null;
    }

    localStorage.setItem(SUBSCRIPTION_KEY, endpoint);
    console.log("Push subscription registered");
    return sub;
  } catch (err) {
    console.warn("Push subscription failed:", err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      // Remove from Supabase
      await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
      localStorage.removeItem(SUBSCRIPTION_KEY);
    }
  } catch (err) {
    console.warn("Push unsubscribe failed:", err);
  }
}

/**
 * Send push notifications to specific users via Edge Function.
 * Call this from the app when creating notifications.
 * @param {string[]} userIds - target user IDs
 * @param {string} title - notification title
 * @param {string} body - notification body
 * @param {object} options - { page, tag }
 */
export async function sendPushToUsers(userIds, title, body, options = {}) {
  if (!userIds?.length || !title) return;

  // Send browser push + Telegram in parallel
  try {
    const telegramBody = {
      user_ids: userIds,
      title,
      body: body || "",
    };
    // Route to per-project Telegram group if chatId provided
    if (options.chatId) {
      telegramBody.chat_id = options.chatId;
    }
    // Include inline keyboard for interactive approvals (e.g. PO approval buttons)
    if (options.reply_markup) {
      telegramBody.reply_markup = options.reply_markup;
    }
    // Include file URL to send document to Telegram
    if (options.file_url) {
      telegramBody.file_url = options.file_url;
    }

    const [pushResult, telegramResult] = await Promise.allSettled([
      supabase.functions.invoke("send-push", {
        body: {
          user_ids: userIds,
          title,
          body: body || "",
          page: options.page || null,
          tag: options.tag || null,
        },
      }),
      supabase.functions.invoke("send-telegram", {
        body: telegramBody,
      }),
    ]);

    if (pushResult.status === "fulfilled") console.log("Push sent:", pushResult.value.data);
    if (telegramResult.status === "fulfilled") console.log("Telegram sent:", telegramResult.value.data);
  } catch (err) {
    console.warn("Notification send error:", err);
  }
}
